import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Search, Phone, Mail, MapPin, MessageCircle, Send, Facebook, Instagram, Bell, ChevronLeft, ChevronRight, Check, LogIn, UserPlus, ShoppingCart, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import useOGMeta from '../hooks/useOGMeta';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShopPublicPage({ forcedSlug }) {
  const params = useParams();
  const slug = forcedSlug || params.slug;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('tutti');
  const [search, setSearch] = useState('');

  useOGMeta({
    title: data?.shop?.name || 'Negozio',
    description: data?.shop?.description || '',
    url: `${window.location.origin}/negozio/${slug}`,
  });

  useEffect(() => {
    axios.get(`${API}/negozio/${slug}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="pb-safe px-4 pt-4 space-y-4">
        <div className="h-48 skeleton-shimmer rounded-xl" />
        <div className="grid grid-cols-2 gap-3">
          {[1,2,3,4].map(i => (
            <div key={i} className="bg-white rounded-2xl border border-[#E8E3D9] overflow-hidden">
              <div className="aspect-[4/5] skeleton-shimmer" />
              <div className="p-3 space-y-2"><div className="h-4 skeleton-shimmer rounded" /><div className="h-5 skeleton-shimmer rounded w-1/2" /></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!data) return <div className="p-8 text-center text-[#877E78]">Negozio non trovato</div>;

  const { shop, products, featured_products, latest_flyer } = data;
  const categories = shop?.categories || [];
  const banners = shop?.home_banners || [];
  const socialWidgets = (shop?.social_widgets || []).filter(w => w.visible && w.handle);

  const filteredProducts = products?.filter(p => {
    const matchCat = category === 'tutti' || p.category === category;
    const matchSearch = !search || p.title?.toLowerCase().includes(search.toLowerCase()) || p.description?.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  }) || [];

  const showFeatured = featured_products?.length > 0 && !search && category === 'tutti';

  return (
    <div data-testid="shop-public-page" className="pb-safe bg-[#FDFBF7] min-h-screen">
      {/* SHOP-OWNED HEADER: logo + nome + utenze visitatore (no riferimenti PhotoVendo) */}
      <ShopOwnedHeader shop={shop} slug={slug} />

      {/* HERO — Banner Carousel */}
      {banners.length > 0 ? (
        <BannerCarousel banners={banners} />
      ) : (
        <ShopHeaderFallback shop={shop} />
      )}

      {/* Shop quick info bar */}
      <div className="bg-white border-b border-[#E8E3D9] px-4 py-3">
        <h1 className="text-xl font-bold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
          {shop?.name}
        </h1>
        {shop?.description && (
          <p className="text-xs text-[#5C554F] mt-0.5">{shop.description}</p>
        )}
        <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-[#877E78]">
          {shop?.phone && (
            <a href={`tel:${shop.phone}`} className="flex items-center gap-1 hover:text-[#C25934]">
              <Phone size={11} /> {shop.phone}
            </a>
          )}
          {shop?.address && (
            <span className="flex items-center gap-1">
              <MapPin size={11} /> {shop.address}
            </span>
          )}
          {shop?.email && (
            <a href={`mailto:${shop.email}`} className="flex items-center gap-1 hover:text-[#C25934]">
              <Mail size={11} /> {shop.email}
            </a>
          )}
        </div>
      </div>

      {/* NEWSLETTER OPT-IN */}
      {shop?.newsletter_enabled && (
        <NewsletterForm slug={slug} />
      )}

      {/* FEATURED: prodotti del volantino più recente */}
      {showFeatured && (
        <div className="px-4 mt-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[10px] font-bold text-[#C25934] uppercase tracking-wider">
                🔥 In evidenza
              </p>
              <h2 className="text-lg font-bold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
                {latest_flyer?.title || 'Volantino di Oggi'}
              </h2>
              <p className="text-[11px] text-[#877E78]">
                {latest_flyer?.date} · {featured_products.length} prodotti
              </p>
            </div>
            <Link
              data-testid="archive-link"
              to={`/negozio/${slug}/archivio`}
              className="text-xs text-[#4A6B53] font-medium hover:underline flex items-center gap-1"
            >
              📚 Archivio →
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {featured_products.map(p => <ProductCard key={p.id} product={p} shopSlug={slug} />)}
          </div>
        </div>
      )}

      {/* Search + Categories */}
      <div className="px-4 pt-6 space-y-3">
        <h2 className="text-sm font-bold text-[#5C554F] uppercase tracking-wider">Tutto il catalogo</h2>
        <div className="flex items-center bg-white rounded-xl border border-[#E8E3D9] overflow-hidden">
          <Search size={16} className="ml-3 text-[#877E78]" />
          <input
            data-testid="shop-search"
            type="text"
            placeholder="Cerca prodotti..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 px-3 py-2.5 text-sm bg-transparent outline-none text-[#2A2624]"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
          <button
            onClick={() => setCategory('tutti')}
            className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.08em] transition-all ${
              category === 'tutti' ? 'bg-[#C25934] text-white' : 'bg-[#F0EBE1] text-[#5C554F]'
            }`}
          >
            Tutti ({products?.length || 0})
          </button>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.08em] transition-all ${
                category === cat ? 'bg-[#C25934] text-white' : 'bg-[#F0EBE1] text-[#5C554F]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Products grid */}
      <div className="px-4 mt-3">
        {filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <Search size={32} className="mx-auto text-[#877E78] mb-3" />
            <p className="text-[#877E78]">Nessun prodotto trovato</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {filteredProducts.map(p => <ProductCard key={p.id} product={p} shopSlug={slug} />)}
          </div>
        )}
      </div>

      {/* SOCIAL WIDGETS BAR */}
      {socialWidgets.length > 0 && (
        <SocialBar widgets={socialWidgets} />
      )}

      {/* Footer indipendente con riferimento PhotoVendo solo in basso */}
      <ShopFooter shop={shop} />
    </div>
  );
}

// ─── Components ───────────────────────────

function BannerCarousel({ banners }) {
  const [idx, setIdx] = useState(0);
  const [urls, setUrls] = useState({});
  const timerRef = useRef(null);
  const N = banners.length;

  useEffect(() => {
    banners.forEach(async b => {
      if (urls[b.id] || !b.image_path) return;
      try {
        const res = await axios.get(`${API}/files/${b.image_path}`, { responseType: 'blob' });
        setUrls(prev => ({ ...prev, [b.id]: URL.createObjectURL(res.data) }));
      } catch { /* ignore */ }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [banners]);

  useEffect(() => {
    if (N <= 1) return;
    timerRef.current = setInterval(() => setIdx(i => (i + 1) % N), 5000);
    return () => clearInterval(timerRef.current);
  }, [N]);

  const go = (i) => setIdx(((i % N) + N) % N);

  const current = banners[idx];

  return (
    <div data-testid="banner-carousel" className="relative bg-[#2A2624] aspect-[16/9] sm:aspect-[21/9] overflow-hidden">
      {banners.map((b, i) => (
        <div
          key={b.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === idx ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        >
          {urls[b.id] && (
            <img src={urls[b.id]} alt={b.title || 'Banner'} className="w-full h-full object-cover" />
          )}
          {(b.title || b.subtitle || b.cta_label) && (
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex flex-col justify-end p-4 sm:p-8">
              {b.title && (
                <h2 className="text-white text-2xl sm:text-4xl font-bold max-w-xl" style={{ fontFamily: 'Outfit', textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
                  {b.title}
                </h2>
              )}
              {b.subtitle && <p className="text-white/90 text-sm sm:text-base mt-1 max-w-xl">{b.subtitle}</p>}
              {b.cta_label && b.cta_url && (
                <a
                  href={b.cta_url}
                  target={b.cta_url.startsWith('http') ? '_blank' : '_self'}
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center justify-center w-fit bg-[#C25934] hover:bg-[#A64B2B] text-white text-sm font-semibold px-5 py-2.5 rounded-full"
                >
                  {b.cta_label} →
                </a>
              )}
            </div>
          )}
        </div>
      ))}
      {N > 1 && (
        <>
          <button
            data-testid="banner-prev"
            onClick={() => go(idx - 1)}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            data-testid="banner-next"
            onClick={() => go(idx + 1)}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center"
          >
            <ChevronRight size={20} />
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {banners.map((_, i) => (
              <button
                key={i}
                onClick={() => go(i)}
                className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-6 bg-white' : 'w-1.5 bg-white/50'}`}
                aria-label={`slide ${i+1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ShopHeaderFallback({ shop }) {
  const [cover, setCover] = useState(null);
  useEffect(() => {
    if (!shop?.cover_path) return;
    axios.get(`${API}/files/${shop.cover_path}`, { responseType: 'blob' })
      .then(r => {
        if (r.data && r.data.type && r.data.type.startsWith('image/')) {
          setCover(URL.createObjectURL(r.data));
        }
      })
      .catch(() => {});
  }, [shop?.cover_path]);
  if (!cover) return null;
  return (
    <div className="aspect-[16/9] sm:aspect-[21/9] overflow-hidden bg-[#F0EBE1]">
      <img src={cover} alt={shop.name} className="w-full h-full object-cover" />
    </div>
  );
}

function NewsletterForm({ slug }) {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const submit = async () => {
    if (!email && !phone) return toast.error('Inserisci email o telefono');
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        shop_slug: slug,
        email: email || undefined,
        phone: phone || undefined,
      });
      setDone(true);
      toast.success('Sei iscritto! Ti avviseremo ai nuovi volantini.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore iscrizione');
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div data-testid="newsletter-success" className="mx-4 mt-4 p-4 bg-[#4A6B53]/10 border border-[#4A6B53]/30 rounded-xl flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-[#4A6B53] text-white flex items-center justify-center">
          <Check size={18} />
        </div>
        <div>
          <p className="text-sm font-semibold text-[#4A6B53]">Iscrizione confermata!</p>
          <p className="text-xs text-[#5C554F]">Ti avviseremo quando esce un nuovo volantino.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="newsletter-form" className="mx-4 mt-4 p-3 bg-gradient-to-br from-[#C25934]/10 to-[#E8843D]/10 border border-[#C25934]/20 rounded-xl">
      {!expanded ? (
        <button
          data-testid="newsletter-expand-btn"
          onClick={() => setExpanded(true)}
          className="w-full flex items-center gap-3 text-left"
        >
          <div className="w-10 h-10 rounded-full bg-[#C25934] text-white flex items-center justify-center flex-shrink-0">
            <Bell size={18} />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-[#2A2624]">Avvisami ai nuovi volantini</p>
            <p className="text-[11px] text-[#5C554F]">Iscriviti gratis · offerte solo per te</p>
          </div>
          <span className="text-[#C25934]">→</span>
        </button>
      ) : (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-[#2A2624]">🔔 Ricevi avvisi offerte</p>
          <Input
            data-testid="newsletter-email"
            type="email"
            placeholder="La tua email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="h-10 rounded-lg border-[#E8E3D9] text-sm bg-white"
          />
          <Input
            data-testid="newsletter-phone"
            type="tel"
            placeholder="...o il tuo numero WhatsApp"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            className="h-10 rounded-lg border-[#E8E3D9] text-sm bg-white"
          />
          <div className="flex gap-2">
            <Button
              onClick={() => setExpanded(false)}
              variant="outline"
              className="flex-1 h-9 rounded-full border-[#E8E3D9] text-xs"
            >
              Annulla
            </Button>
            <Button
              data-testid="newsletter-submit"
              onClick={submit}
              disabled={submitting}
              className="flex-1 h-9 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white text-xs font-semibold"
            >
              {submitting ? 'Iscrizione...' : 'Iscrivimi'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SocialBar({ widgets }) {
  const ICONS = {
    whatsapp: { Icon: MessageCircle, color: '#25D366', href: h => `https://wa.me/${h.replace(/\D/g, '')}` },
    telegram: { Icon: Send, color: '#0088cc', href: h => (h.startsWith('http') ? h : `https://t.me/${h.replace(/^@/, '').replace('t.me/', '')}`) },
    instagram: { Icon: Instagram, color: '#E1306C', href: h => `https://instagram.com/${h.replace(/^@/, '')}` },
    facebook: { Icon: Facebook, color: '#1877F2', href: h => (h.startsWith('http') ? h : `https://facebook.com/${h}`) },
    tiktok: { Icon: null, color: '#000000', href: h => `https://tiktok.com/@${h.replace(/^@/, '')}` },
  };
  return (
    <div data-testid="social-bar" className="mx-4 mt-8 p-4 bg-white border border-[#E8E3D9] rounded-2xl">
      <p className="text-sm font-semibold text-[#2A2624] text-center mb-3" style={{ fontFamily: 'Outfit' }}>
        📲 Seguici sui nostri canali
      </p>
      <div className="flex justify-center gap-3 flex-wrap">
        {widgets.map(w => {
          const cfg = ICONS[w.platform];
          if (!cfg) return null;
          const { Icon } = cfg;
          return (
            <a
              key={w.platform}
              data-testid={`social-bar-${w.platform}`}
              href={cfg.href(w.handle)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-1 px-3 py-2 rounded-xl hover:bg-[#F0EBE1] transition-colors"
            >
              <div className="w-11 h-11 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: cfg.color }}>
                {Icon ? <Icon size={20} /> : <span className="text-sm font-bold">TT</span>}
              </div>
              <span className="text-[10px] font-medium text-[#5C554F] capitalize">{w.platform}</span>
            </a>
          );
        })}
      </div>
    </div>
  );
}

function ShopOwnedHeader({ shop, slug }) {
  const { user, logout } = useAuth();
  const [logoUrl, setLogoUrl] = useState(null);
  useEffect(() => {
    if (!shop?.logo_path) return;
    axios.get(`${API}/files/${shop.logo_path}`, { responseType: 'blob' })
      .then(r => {
        if (r.data && r.data.type && r.data.type.startsWith('image/')) {
          setLogoUrl(URL.createObjectURL(r.data));
        }
      })
      .catch(() => {});
  }, [shop?.logo_path]);

  const shopHome = `/negozio/${slug}`;
  const shopCart = `/carrello?from=${encodeURIComponent(slug || '')}`;
  const shopAccount = `/account?from=${encodeURIComponent(slug || '')}`;
  const shopName = shop?.name || 'Negozio';
  const initials = shopName.slice(0, 2).toUpperCase();

  return (
    <header data-testid="shop-owned-header" className="bg-white border-b border-[#E8E3D9] sticky top-0 z-30">
      <div className="px-4 py-3 flex items-center gap-3">
        {/* SHOP brand — logo + name, link to shop home only */}
        <Link to={shopHome} data-testid="shop-brand-link" className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-full bg-[#F0EBE1] border border-[#E8E3D9] overflow-hidden flex items-center justify-center flex-shrink-0">
            {logoUrl ? (
              <img src={logoUrl} alt={shopName} className="w-full h-full object-cover" />
            ) : (
              <span className="text-[#C25934] font-bold text-sm" style={{ fontFamily: 'Outfit' }}>{initials}</span>
            )}
          </div>
          <div className="min-w-0">
            <div className="text-base font-bold text-[#2A2624] truncate" style={{ fontFamily: 'Outfit' }}>
              {shopName}
            </div>
            {!user && (
              <div className="text-[10px] text-[#877E78] -mt-0.5">Ospite</div>
            )}
            {user && (
              <div className="text-[10px] text-[#877E78] -mt-0.5 truncate">{user.name || user.email}</div>
            )}
          </div>
        </Link>

        {/* Visitor actions (Cart + Login/Account) */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            to={shopCart}
            data-testid="shop-visitor-cart"
            aria-label="Carrello"
            className="w-10 h-10 rounded-full bg-[#F0EBE1] hover:bg-[#E8E3D9] flex items-center justify-center text-[#2A2624] transition-colors"
          >
            <ShoppingCart size={18} />
          </Link>
          {!user ? (
            <>
              <Link
                to={shopAccount}
                data-testid="shop-visitor-login"
                className="hidden sm:flex items-center gap-1 px-3 h-10 rounded-full bg-[#F0EBE1] hover:bg-[#E8E3D9] text-[#2A2624] text-xs font-semibold"
              >
                <LogIn size={14} /> Accedi
              </Link>
              <Link
                to={`${shopAccount}&mode=register`}
                data-testid="shop-visitor-register"
                className="flex items-center gap-1 px-3 h-10 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white text-xs font-semibold"
              >
                <UserPlus size={14} /> <span className="hidden sm:inline">Registrati</span>
              </Link>
            </>
          ) : (
            <>
              <Link
                to={shopAccount}
                data-testid="shop-visitor-account"
                aria-label="Account"
                className="w-10 h-10 rounded-full bg-[#F0EBE1] hover:bg-[#E8E3D9] flex items-center justify-center text-[#2A2624]"
              >
                <User size={18} />
              </Link>
              <button
                onClick={logout}
                data-testid="shop-visitor-logout"
                className="hidden sm:block text-xs text-[#877E78] hover:text-[#C25934] px-2"
              >
                Esci
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function ShopFooter({ shop }) {
  const shopName = shop?.name || 'Negozio';
  const year = new Date().getFullYear();
  return (
    <footer data-testid="shop-footer" className="mt-8 bg-[#2A2624] text-[#D5CFC4]">
      <div className="px-4 py-6 space-y-4">
        <div>
          <div className="text-base font-bold text-white" style={{ fontFamily: 'Outfit' }}>{shopName}</div>
          {shop?.description && (
            <p className="text-xs opacity-70 mt-1 max-w-md">{shop.description}</p>
          )}
        </div>

        <div className="text-[11px] space-y-1 opacity-80">
          {shop?.phone && <div>📞 {shop.phone}</div>}
          {shop?.email && <div>✉️ {shop.email}</div>}
          {shop?.address && <div>📍 {shop.address}</div>}
          {shop?.vat_number && <div className="opacity-60">P.IVA {shop.vat_number}</div>}
        </div>

        <div className="text-[10px] opacity-50 pt-3 border-t border-white/10">
          © {year} {shopName}. Tutti i diritti riservati.
        </div>
        <div className="text-[10px] opacity-40 text-center">
          Sito powered by <a href="https://photovendo.com" target="_blank" rel="noopener noreferrer" className="opacity-80 hover:opacity-100 underline">PhotoVendo</a>
        </div>
      </div>
    </footer>
  );
}

